import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Sparkles, ArrowLeft, ArrowRight, Check, Clock, Star, ChevronRight,
  Sun, Moon
} from 'lucide-react';
import { useEffect, useState } from 'react';
import SEOHead from './SEOHead';
import { useLanguage } from '../hooks/useLanguage';
import { buildBreadcrumb, buildFAQSchema, buildProductSchema, SITE } from '../config/seo';

/**
 * Shared layout for vertical SEO landing pages (/restaurants, /cafes, /food-trucks).
 * Each page passes a config object with bilingual content. The component handles
 * SEO head (Schema.org Product + FAQ + Breadcrumb), responsive layout, dark/light
 * theming, and CTAs.
 */
export default function VerticalLanding({ config }) {
  const { i18n } = useTranslation();
  const { isAr, toggleLanguage } = useLanguage();
  const lang = isAr ? 'ar' : 'en';
  const c = config[lang];
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const saved = localStorage.getItem('zouqai_theme') || 'light';
    setTheme(saved);
    if (saved === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('zouqai_theme', next);
    if (next === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  };

  // Build Schema.org graph for this vertical
  const productSchema = buildProductSchema({
    name: c.h1,
    description: c.intro,
    url: `${SITE.url}${config.path}`,
  });
  const faqSchema = buildFAQSchema(c.faqs);
  const breadcrumb = buildBreadcrumb([
    { name: isAr ? 'الرئيسية' : 'Home', url: SITE.url },
    { name: c.breadcrumb, url: `${SITE.url}${config.path}` },
  ]);

  const Arrow = isAr ? ArrowLeft : ArrowRight;
  const accent = config.accent;

  return (
    <div
      className={`min-h-screen transition-colors ${theme === 'dark' ? 'bg-[#0a0e1a] text-white' : 'bg-white text-slate-900'}`}
      data-testid={`vertical-page-${config.id}`}
      style={{ '--lp-rgb': theme === 'dark' ? '255,255,255' : '15,23,42' }}
    >
      <SEOHead
        title={c.metaTitle}
        titleEn={config.en.metaTitle}
        description={c.metaDescription}
        descriptionEn={config.en.metaDescription}
        path={config.path}
        keywords={c.keywords}
        schemas={[productSchema, faqSchema, breadcrumb]}
        lang={lang}
      />

      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{ background: theme === 'dark' ? 'rgba(10,14,26,0.85)' : 'rgba(255,255,255,0.85)', borderColor: 'rgba(var(--lp-rgb),0.06)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="vertical-logo">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-sm" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)` }}>ذ</div>
            <span className="font-black text-base sm:text-lg" style={{ fontFamily: 'Cairo, sans-serif' }}>{isAr ? 'ذوق AI' : 'Zouq AI'}</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/restaurants" className="hover:opacity-100 transition" style={{ opacity: config.id === 'restaurants' ? 1 : 0.7 }}>{isAr ? 'مطاعم' : 'Restaurants'}</Link>
            <Link to="/cafes" className="hover:opacity-100 transition" style={{ opacity: config.id === 'cafes' ? 1 : 0.7 }}>{isAr ? 'كافيهات' : 'Cafés'}</Link>
            <Link to="/food-trucks" className="hover:opacity-100 transition" style={{ opacity: config.id === 'food_trucks' ? 1 : 0.7 }}>{isAr ? 'عربات متنقلة' : 'Food Trucks'}</Link>
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5 transition" data-testid="vertical-theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={toggleLanguage} className="px-3 h-9 rounded-lg text-xs font-bold hover:bg-black/5 dark:hover:bg-white/5 transition" data-testid="vertical-lang-toggle">
              {isAr ? 'EN' : 'عربي'}
            </button>
            <Link
              to="/demo"
              className="hidden sm:inline-flex items-center gap-1 px-4 h-9 rounded-lg text-xs font-bold text-white transition hover:scale-[1.02]"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)`, boxShadow: `0 4px 12px ${accent}40` }}
              data-testid="vertical-cta-header"
            >
              {isAr ? 'ابدأ مجاناً' : 'Start Free'} <Arrow className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(circle at 50% 0%, ${accent}1A, transparent 60%)` }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-12 text-center">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold mb-5"
            style={{ background: `${accent}1A`, color: accent, border: `1px solid ${accent}33` }}
          >
            <Sparkles className="w-3 h-3" /> {c.eyebrow}
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-5"
            style={{ fontFamily: 'Cairo, sans-serif' }}
            data-testid="vertical-h1"
          >
            {c.h1}
          </h1>
          <p
            className="text-base sm:text-lg max-w-3xl mx-auto mb-8"
            style={{ color: 'rgba(var(--lp-rgb),0.7)', lineHeight: 1.8 }}
          >
            {c.intro}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/demo"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 h-12 rounded-xl text-sm font-bold text-white transition hover:scale-[1.02]"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)`, boxShadow: `0 8px 24px ${accent}40` }}
              data-testid="vertical-cta-primary"
            >
              <Sparkles className="w-4 h-4" /> {c.ctaPrimary}
            </Link>
            <Link
              to="/"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 h-12 rounded-xl text-sm font-bold transition border"
              style={{ borderColor: 'rgba(var(--lp-rgb),0.15)', color: 'rgba(var(--lp-rgb),0.85)' }}
              data-testid="vertical-cta-secondary"
            >
              {c.ctaSecondary}
            </Link>
          </div>
          <div className="mt-6 flex items-center justify-center gap-6 text-[12px]" style={{ color: 'rgba(var(--lp-rgb),0.55)' }}>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" style={{ color: accent }} /> {isAr ? 'بدون بطاقة ائتمان' : 'No credit card'}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" style={{ color: accent }} /> {isAr ? '14 يوم مجاناً' : '14 days free'}</span>
            <span className="inline-flex items-center gap-1.5 hidden sm:inline-flex"><Star className="w-3.5 h-3.5" style={{ color: accent }} /> 4.8/5 ({isAr ? '127 تقييم' : '127 reviews'})</span>
          </div>
        </div>
      </section>

      {/* "Start in 60 seconds" — speed-of-onboarding pitch */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold mb-3" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)' }}>
              <Clock className="w-3 h-3" /> {isAr ? 'ابدأ في 60 ثانية' : 'Start in 60 seconds'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-black mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {c.fastStartTitle}
            </h2>
            <p className="text-sm sm:text-base max-w-2xl mx-auto" style={{ color: 'rgba(var(--lp-rgb),0.65)' }}>
              {c.fastStartDesc}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.fastStartSteps.map((step, idx) => (
              <div
                key={idx}
                className="relative p-6 rounded-2xl border"
                style={{ background: 'rgba(var(--lp-rgb),0.02)', borderColor: 'rgba(var(--lp-rgb),0.08)' }}
                data-testid={`fast-start-step-${idx + 1}`}
              >
                <div className="absolute -top-3 inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-black" style={{ background: accent, [isAr ? 'right' : 'left']: 16 }}>
                  {idx + 1}
                </div>
                <div className="text-base font-bold mb-2 mt-2" style={{ fontFamily: 'Cairo, sans-serif' }}>{step.title}</div>
                <p className="text-sm" style={{ color: 'rgba(var(--lp-rgb),0.65)', lineHeight: 1.7 }}>{step.desc}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold" style={{ color: accent }}>
                  <Clock className="w-3 h-3" /> {step.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid — vertical-specific */}
      <section className="px-4 sm:px-6 py-12 sm:py-16" style={{ background: 'rgba(var(--lp-rgb),0.02)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-black mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {c.featuresTitle}
            </h2>
            <p className="text-sm sm:text-base max-w-2xl mx-auto" style={{ color: 'rgba(var(--lp-rgb),0.65)' }}>
              {c.featuresDesc}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.features.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div
                  key={idx}
                  className="p-6 rounded-2xl border transition hover:scale-[1.02]"
                  style={{ background: 'rgba(var(--lp-rgb),0.03)', borderColor: 'rgba(var(--lp-rgb),0.08)' }}
                  data-testid={`vertical-feature-${idx}`}
                >
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accent}1A` }}>
                    <Icon className="w-5 h-5" style={{ color: accent }} />
                  </div>
                  <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>{f.title}</h3>
                  <p className="text-sm" style={{ color: 'rgba(var(--lp-rgb),0.65)', lineHeight: 1.7 }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sub-types served (e.g. Shawarma, Burger… / Specialty Coffee, Bakery…) */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-black mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {c.subtypesTitle}
            </h2>
            <p className="text-sm sm:text-base max-w-2xl mx-auto" style={{ color: 'rgba(var(--lp-rgb),0.65)' }}>
              {c.subtypesDesc}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {c.subtypes.map((t, idx) => (
              <span
                key={idx}
                className="px-3 py-1.5 rounded-full text-xs font-bold border"
                style={{ background: `${accent}0A`, color: accent, borderColor: `${accent}33` }}
                data-testid={`vertical-subtype-${idx}`}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* GCC markets coverage */}
      <section className="px-4 sm:px-6 py-12 sm:py-16" style={{ background: 'rgba(var(--lp-rgb),0.02)' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {c.gccTitle}
          </h2>
          <p className="text-sm sm:text-base max-w-2xl mx-auto mb-8" style={{ color: 'rgba(var(--lp-rgb),0.65)' }}>
            {c.gccDesc}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {c.gccMarkets.map((m, idx) => (
              <div
                key={idx}
                className="px-4 py-3 rounded-xl border min-w-[140px]"
                style={{ background: 'rgba(var(--lp-rgb),0.03)', borderColor: 'rgba(var(--lp-rgb),0.08)' }}
              >
                <div className="text-2xl mb-1">{m.flag}</div>
                <div className="text-xs font-bold">{m.name}</div>
                <div className="text-[10px]" style={{ color: 'rgba(var(--lp-rgb),0.5)' }}>{m.currency}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-center mb-10" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {c.faqTitle}
          </h2>
          <div className="space-y-3">
            {c.faqs.map((f, idx) => (
              <details
                key={idx}
                className="group rounded-2xl border overflow-hidden"
                style={{ background: 'rgba(var(--lp-rgb),0.02)', borderColor: 'rgba(var(--lp-rgb),0.08)' }}
                data-testid={`vertical-faq-${idx}`}
              >
                <summary className="flex items-center justify-between cursor-pointer px-5 py-4 text-sm font-bold" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {f.q}
                  <ChevronRight className="w-4 h-4 group-open:rotate-90 transition" style={{ color: accent }} />
                </summary>
                <div className="px-5 pb-4 text-sm" style={{ color: 'rgba(var(--lp-rgb),0.7)', lineHeight: 1.8 }}>{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 sm:px-6 py-16 sm:py-24">
        <div
          className="max-w-4xl mx-auto text-center p-10 sm:p-14 rounded-3xl border"
          style={{ background: `linear-gradient(135deg, ${accent}10, ${accent}05)`, borderColor: `${accent}33` }}
        >
          <h2 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {c.finalCtaTitle}
          </h2>
          <p className="text-sm sm:text-base mb-8 max-w-xl mx-auto" style={{ color: 'rgba(var(--lp-rgb),0.7)' }}>
            {c.finalCtaDesc}
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 px-8 h-14 rounded-xl text-base font-bold text-white transition hover:scale-[1.02]"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)`, boxShadow: `0 12px 32px ${accent}50` }}
            data-testid="vertical-cta-final"
          >
            <Sparkles className="w-5 h-5" /> {c.ctaPrimary}
          </Link>
        </div>
      </section>

      {/* Hidden SEO content for crawlers — additional keyword-rich body */}
      <section className="px-4 sm:px-6 py-12 sm:py-16" style={{ background: 'rgba(var(--lp-rgb),0.02)' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-black mb-5" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {c.seoTitle}
          </h2>
          <div className="space-y-4 text-sm leading-loose" style={{ color: 'rgba(var(--lp-rgb),0.75)' }}>
            {c.seoBody.map((p, idx) => (
              <p key={idx} data-testid={`vertical-seo-paragraph-${idx}`}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 sm:px-6 py-10 border-t" style={{ borderColor: 'rgba(var(--lp-rgb),0.08)' }}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs" style={{ color: 'rgba(var(--lp-rgb),0.5)' }}>
          <div>© 2026 {isAr ? 'ذوق AI' : 'Zouq AI'} — {isAr ? 'كل الحقوق محفوظة' : 'All rights reserved'}</div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:underline">{isAr ? 'الرئيسية' : 'Home'}</Link>
            <Link to="/restaurants" className="hover:underline">{isAr ? 'مطاعم' : 'Restaurants'}</Link>
            <Link to="/cafes" className="hover:underline">{isAr ? 'كافيهات' : 'Cafés'}</Link>
            <Link to="/food-trucks" className="hover:underline">{isAr ? 'عربات' : 'Food Trucks'}</Link>
            <Link to="/privacy" className="hover:underline">{isAr ? 'الخصوصية' : 'Privacy'}</Link>
            <Link to="/terms" className="hover:underline">{isAr ? 'الشروط' : 'Terms'}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
